import { useState } from 'react';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';

interface ReleasePayoutModalProps {
  providerName: string;
  providerRole: string;
  netPayout: string;
  relatedJobIds: string[];
  paymentMethod: string;
  bankAccount: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (confirmChecks: boolean) => void;
}

const ReleasePayoutModal = ({
  providerName,
  providerRole,
  netPayout,
  relatedJobIds,
  paymentMethod,
  bankAccount,
  isOpen,
  onClose,
  onConfirm,
}: ReleasePayoutModalProps) => {
  const [confirmChecks, setConfirmChecks] = useState(false);

  const handleConfirm = () => {
    if (confirmChecks) {
      onConfirm(confirmChecks);
      handleClose();
    }
  };

  const handleClose = () => {
    setConfirmChecks(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Release Payout</h2>
              <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mt-1">Financial Operations</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Provider Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-teal-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {providerName.charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{providerName}</p>
              <p className="text-sm text-gray-600">◉ {providerRole}</p>
            </div>
          </div>

          {/* Net Payout Display */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 p-6 rounded">
            <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Net Payout</p>
            <p className="text-4xl font-bold text-gray-900">{netPayout}</p>
          </div>

          {/* Related Job IDs */}
          <div>
            <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-3">
              Related Job IDs
            </label>
            <div className="grid grid-cols-2 gap-3">
              {relatedJobIds.map((jobId) => (
                <div key={jobId} className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold text-gray-900 text-sm">
                  {jobId}
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method */}
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-200">
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
                Payment Method
              </label>
              <p className="font-semibold text-gray-900">{paymentMethod}</p>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
                Bank Account
              </label>
              <p className="font-semibold text-gray-900">{bankAccount}</p>
            </div>
          </div>

          {/* Compliance Confirmation */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <input
              type="checkbox"
              id="confirmChecks"
              checked={confirmChecks}
              onChange={(e) => setConfirmChecks(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 mt-1"
            />
            <label htmlFor="confirmChecks" className="text-sm text-gray-700 cursor-pointer">
              I confirm that all background checks, job completion signatures, and compliance documentation have been verified for the listed jobs. I authorize the immediate release of funds to the provider.
            </label>
          </div>

          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-700 font-medium">
              Funds typically arrive in the provider's account within 2-4 business hours.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3">
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-6 py-2 text-gray-600 font-semibold hover:text-gray-800"
            >
              Cancel & Review
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-2 text-teal-600 font-semibold hover:text-teal-700 border border-teal-300 rounded-lg"
            >
              View Audit Log
            </button>
          </div>
          <button
            onClick={handleConfirm}
            disabled={!confirmChecks}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            ✓ Confirm Release
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleasePayoutModal;
